import { Capacitor } from '@capacitor/core';
import { parseBowQr } from '@/lib/qrScan';

/**
 * Deep links do app nativo (App Links/Universal Links https + scheme
 * br.app.bow://). O SO entrega a URL via @capacitor/app; daqui ela vira rota
 * interna pelo MESMO parseBowQr do scanner embarcado — /qr/{hub}/{n}
 * (auto-start via QRHandler), /qr/{hub} → /scan/{hub}, /v/{id} → aspirador.
 * Deslogado, o RequireAuth manda pro /login com state.from e devolve ao
 * destino depois.
 *
 * ⚠️ Shells 1.1.x NÃO têm o plugin @capacitor/app: este módulo chega neles
 * por bundle OTA e não pode quebrar — daí as 3 camadas de guard
 * (isNativePlatform → isPluginAvailable → try/catch) e o import dinâmico.
 * Nunca reter/await o proxy do plugin em si (gotcha da release 1.1.0): await
 * só em import() e em chamada de método.
 */

const CUSTOM_SCHEME = 'br.app.bow';

// appUrlOpen e getLaunchUrl podem entregar a MESMA URL no cold start — dedup
// por URL + janela curta evita navegação dupla.
let lastHandled: { url: string; at: number } | null = null;

/** Normaliza a URL recebida do SO pro path interno, ou null se não for Bow.
 *  No scheme custom o 1º segmento vira authority (br.app.bow://qr/X/1 →
 *  host "qr") — recompomos como https://bow.app.br/qr/X/1 antes do parse. */
export function normalizeDeepLink(
  raw: string | undefined | null
): string | null {
  const url = (raw ?? '').trim();
  if (!url) return null;
  if (url.startsWith(`${CUSTOM_SCHEME}://`)) {
    const rest = url.slice(`${CUSTOM_SCHEME}://`.length);
    return parseBowQr(`https://bow.app.br/${rest}`);
  }
  return parseBowQr(url);
}

/** Registra appUrlOpen (warm start) + getLaunchUrl (cold start). Chamar de
 *  dentro do Router (o navigate precisa do contexto). No-op no web/PWA e em
 *  shells sem o plugin. */
export async function initDeepLinks(
  navigate: (path: string) => void
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!Capacitor.isPluginAvailable('App')) return;
  try {
    const { App } = await import('@capacitor/app');
    const handle = (raw: string | undefined) => {
      const path = normalizeDeepLink(raw);
      if (!path) return;
      const now = Date.now();
      if (lastHandled && lastHandled.url === raw && now - lastHandled.at < 3000) {
        return;
      }
      lastHandled = { url: raw ?? '', at: now };
      navigate(path);
    };
    void App.addListener('appUrlOpen', (event) => handle(event?.url));
    const launch = await App.getLaunchUrl();
    if (launch?.url) handle(launch.url);
  } catch {
    // Plugin indisponível/erro — deep link silenciosamente inativo.
  }
}
